import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus, PencilSimple, Trash, Sparkle } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { EditClassDialog } from './EditClassDialog'

interface GameClass {
  id: string
  name: string
  description: string
  icon: string
  starting_points: number
}

interface ClassesTabProps {
  tableId: string
  classes: GameClass[]
  isGameMaster: boolean
  onClassesChange: () => void
  onSaveClass: (classData: Omit<GameClass, 'id'> & { id?: string }) => Promise<void>
  onDeleteClass: (classId: string) => Promise<void>
}

export function ClassesTab({ tableId, classes, isGameMaster, onClassesChange, onSaveClass, onDeleteClass }: ClassesTabProps) {
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editingClass, setEditingClass] = useState<GameClass | null>(null)
  const [isDeleting, setIsDeleting] = useState<string | null>(null)

  const handleCreateClass = () => {
    setEditingClass(null)
    setShowEditDialog(true)
  }

  const handleEditClass = (classData: GameClass) => {
    setEditingClass(classData)
    setShowEditDialog(true)
  }

  const handleDeleteClass = async (classId: string, className: string) => {
    if (!confirm(`Tem certeza que deseja excluir a classe "${className}"?\n\nEsta ação não pode ser desfeita.`)) {
      return
    }

    setIsDeleting(classId)
    try {
      await onDeleteClass(classId)
      toast.success(`Classe "${className}" excluída com sucesso`)
      onClassesChange()
    } catch (error: any) {
      console.error('Erro ao excluir classe:', error)
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao excluir classe'
      toast.error(errorMessage)
    } finally {
      setIsDeleting(null)
    }
  }

  const handleSaveClass = async (classData: Omit<GameClass, 'id'> & { id?: string }) => {
    await onSaveClass(classData)
    toast.success(editingClass ? 'Classe atualizada com sucesso' : 'Classe criada com sucesso')
    onClassesChange()
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Classes da Campanha</h3>
          <p className="text-sm text-muted-foreground">
            Gerencie as classes disponíveis para personagens
          </p>
        </div>
        {isGameMaster && (
          <Button onClick={handleCreateClass}>
            <Plus size={16} className="mr-2" />
            Nova Classe
          </Button>
        )}
      </div>

      {classes.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Sparkle size={32} className="text-muted-foreground" />
            </div>
            <h4 className="text-lg font-medium mb-2">Nenhuma classe configurada</h4>
            <p className="text-muted-foreground mb-4 max-w-md">
              {isGameMaster 
                ? 'Crie classes personalizadas para sua campanha. Cada classe define as características iniciais dos personagens.'
                : 'O mestre ainda não configurou classes para esta campanha.'}
            </p>
            {isGameMaster && (
              <Button onClick={handleCreateClass}>
                <Plus size={16} className="mr-2" />
                Criar Primeira Classe
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map((classData) => (
            <Card key={classData.id} className="group hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">{classData.icon}</div>
                    <div>
                      <CardTitle className="text-lg">{classData.name}</CardTitle>
                      <Badge variant="secondary" className="mt-1">
                        {classData.starting_points} pontos iniciais
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <CardDescription className="line-clamp-3">
                  {classData.description}
                </CardDescription>
                
                {isGameMaster && (
                  <div className="flex gap-2 pt-2 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => handleEditClass(classData)}
                    >
                      <PencilSimple size={14} className="mr-1" />
                      Editar
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleDeleteClass(classData.id, classData.name)}
                      disabled={isDeleting === classData.id}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <EditClassDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        classData={editingClass}
        onSave={handleSaveClass}
      />
    </>
  )
}
